import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const apiFetch = (url, opts = {}) => {
  const token = getToken();
  if (token && !opts.headers) opts.headers = {};
  if (token) opts.headers['x-token'] = token;
  return fetch(url, opts);
};

export default function ManualsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectCode, setSelectedProjectCode] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedManual, setExpandedManual] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [editModule, setEditModule] = useState('');
  const [projectModules, setProjectModules] = useState([]); // 当前项目的模块列表
  const [moduleSearch, setModuleSearch] = useState('');
  const [showModuleDrop, setShowModuleDrop] = useState(false);

  // 加载项目列表
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    const token = getToken();
    const res = await fetch(`${API_URL}/api/workorder/projects`, {
      headers: token ? { 'x-token': token } : {}
    });
    const data = await res.json();
    if (data.success) {
      // 后端返回字符串数组，转换为对象数组，用projectCodes映射真实code
      const projectList = (data.projects || []).map(p => {
        if (typeof p === 'string') {
          return { name: p, code: data.projectCodes?.[p] || p }; // 用真实projectCode
        }
        return p;
      });
      setProjects(projectList);
      if (projectList.length > 0 && !selectedProject) {
        setSelectedProject(projectList[0].name);
        setSelectedProjectCode(projectList[0].code);
      }
    }
  };

  // 加载手册列表
  useEffect(() => {
    if (selectedProject) {
      fetchManuals();
    }
    if (selectedProjectCode) {
      loadProjectModules(selectedProjectCode);
    }
  }, [selectedProject, selectedProjectCode]);

  const loadProjectModules = async (projectCode) => {
    try {
      const res = await apiFetch(`${API_URL}/api/workorder-import/project-modules/${projectCode}`);
      const data = await res.json();
      if (data.success) {
        const flat = flattenModules(data.tree || []);
        setProjectModules(flat);
      }
    } catch (e) {
      console.error('加载模块失败', e);
      setProjectModules([]);
    }
  };

  const flattenModules = (tree) => {
    const result = []; // [{ id, label }]
    for (const node of tree) {
      result.push(node);
      if (node.children) result.push(...flattenModules(node.children));
    }
    return result;
  };

  const fetchManuals = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/manuals/${selectedProjectCode}`);
      const data = await res.json();
      if (data.success) {
        setManuals(data.manuals || []);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // 上传手册
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProject) return;

    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.doc') && !ext.endsWith('.docx') && !ext.endsWith('.pdf')) {
      alert('只支持 Word/PDF 文档');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectCode', selectedProjectCode);

      const res = await apiFetch(`${API_URL}/api/manuals/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        alert('上传成功');
        fetchManuals();
      } else {
        alert(data.error || '上传失败');
      }
    } catch (e) {
      alert('上传失败');
    }
    setUploading(false);
    e.target.value = '';
  };

  // 删除手册
  const handleDelete = async (manualId) => {
    if (!window.confirm('确认删除该手册？')) return;
    try {
      const res = await apiFetch(`${API_URL}/api/manuals/${selectedProjectCode}/${manualId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchManuals();
      } else {
        alert(data.error || '删除失败');
      }
    } catch (e) {
      alert('删除失败');
    }
  };

  // 下载手册原文件
  const handleDownload = async (manualId) => {
    try {
      const res = await apiFetch(`${API_URL}/api/manuals/${selectedProjectCode}/${manualId}/download`);
      const data = await res.json();
      if (data.success && data.url) {
        // 创建临时链接下载
        const a = document.createElement('a');
        a.href = data.url;
        a.download = data.filename || '操作手册';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert(data.error || '获取下载链接失败');
      }
    } catch (e) {
      alert('下载失败');
    }
  };

  // 更新章节模块映射
  const handleSaveModule = async (chapterNum) => {
    try {
      const manualId = expandedManual;
      const res = await apiFetch(`${API_URL}/api/manuals/${selectedProjectCode}/${manualId}/chapters/${chapterNum}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module: editModule })
      });
      const data = await res.json();
      if (data.success) {
        setEditingChapter(null);
        fetchManuals();
      } else {
        alert(data.error || '更新失败');
      }
    } catch (e) {
      alert('更新失败');
    }
  };

  const selectedProjectName = projects.find(p => p.name === selectedProject)?.name || selectedProject;
  
  // 过滤项目列表
  const filteredProjects = projects.filter(p => 
    !projectSearch || 
    p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    p.code.toLowerCase().includes(projectSearch.toLowerCase())
  );

  return (
    <div className="manuals-page">
      <div className="woi-header">
        <button onClick={() => navigate('/')} className="btn-back">← 返回首页</button>
        <h2>📖 操作手册管理</h2>
      </div>

      <div className="manuals-layout">
        {/* 左侧：项目列表 */}
        <div className="manuals-sidebar">
          <div className="manuals-sidebar-title">
            <span>项目列表</span>
            <input 
              type="text" 
              className="manuals-project-search"
              placeholder="搜索项目..."
              value={projectSearch}
              onChange={e => setProjectSearch(e.target.value)}
            />
          </div>
          <div className="manuals-project-list">
            {filteredProjects.map(p => (
              <div
                key={p.code}
                className={`manuals-project-item ${selectedProject === p.name ? 'active' : ''}`}
                onClick={() => { setSelectedProject(p.name); setSelectedProjectCode(p.code); }}
              >
                <span className="project-name">{p.name}</span>
                <span className="project-code">{p.code}</span>
              </div>
            ))}
            {filteredProjects.length === 0 && projectSearch && (
              <div className="manuals-no-results">无匹配项目</div>
            )}
          </div>
        </div>

        {/* 右侧：手册列表 */}
        <div className="manuals-content">
          <div className="manuals-toolbar">
            <span className="manuals-current-project">
              当前项目：{selectedProjectName}
            </span>
            {selectedProject && (
              <label className="btn-primary upload-btn">
                {uploading ? '⏳ 解析中...' : '📤 上传手册'}
                <input
                  type="file"
                  accept=".doc,.docx,.pdf"
                  onChange={handleUpload}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {loading ? (
            <div className="manuals-loading">加载中...</div>
          ) : manuals.length === 0 ? (
            <div className="manuals-empty">
              <div className="manuals-empty-icon">📚</div>
              <div>该项目暂无操作手册</div>
              <div className="manuals-empty-hint">点击"上传手册"添加 Word 或 PDF 文档</div>
            </div>
          ) : (
            <div className="manuals-list">
              {manuals.map(manual => (
                <div key={manual.id} className="manuals-card">
                  <div className="manuals-card-header" onClick={() => setExpandedManual(expandedManual === manual.id ? null : manual.id)}>
                    <div className="manuals-card-info">
                      <span className="manuals-card-icon">📄</span>
                      <div>
                        <div className="manuals-card-name">{manual.originalName}</div>
                        <div className="manuals-card-meta">
                          {manual.totalPages}页 · {manual.chapters.length}章 · {new Date(manual.uploadedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="manuals-card-actions">
                      <button className="btn-download" onClick={(e) => { e.stopPropagation(); handleDownload(manual.id); }}>📥 下载</button>
                      <button className="btn-delete" onClick={(e) => { e.stopPropagation(); handleDelete(manual.id); }}>删除</button>
                      <span className="expand-icon">{expandedManual === manual.id ? '▼' : '▶'}</span>
                    </div>
                  </div>

                  {expandedManual === manual.id && (
                    <div className="manuals-chapters">
                      <div className="chapters-header">
                        <span className="chapters-col-chapter">章节</span>
                        <span className="chapters-col-title">标题</span>
                        <span className="chapters-col-pages">页码范围</span>
                        <span className="chapters-col-module">关联模块</span>
                        <span className="chapters-col-action">操作</span>
                      </div>
                      {manual.chapters.map(ch => (
                        <div key={ch.chapter} className="chapter-row">
                          <span className="chapter-num">第{ch.chapter}章</span>
                          <span className="chapter-title">{ch.title}</span>
                          <span className="chapter-pages">{ch.startPage}-{ch.endPage}</span>
                          {editingChapter === ch.chapter && (
                          <div className="edit-overlay" onClick={() => setEditingChapter(null)} />
                        )}
                        {editingChapter === ch.chapter ? (
                            <span className="chapter-module editing">
                              <div style={{fontWeight:600, marginBottom:8}}>编辑模块映射</div>
                              <div className="fuzzy-select-wrapper">
                                <input
                                  value={moduleSearch || editModule}
                                  onChange={e => { setModuleSearch(e.target.value); setShowModuleDrop(true); setEditModule(e.target.value); }}
                                  onFocus={() => setShowModuleDrop(true)}
                                  placeholder="搜索模块..."
                                  className="chapter-edit-input"
                                  autoFocus
                                  onClick={e => e.stopPropagation()}
                                />
                                {showModuleDrop && projectModules.length > 0 && (
                                  <div className="fuzzy-select-dropdown">
                                    {projectModules
                                      .filter(m => !moduleSearch || m.label.includes(moduleSearch))
                                      .map(m => (
                                        <div key={m.id} className="fuzzy-select-option"
                                          onMouseDown={() => { setEditModule(m.label); setModuleSearch(''); setShowModuleDrop(false); }}>
                                          {m.label}
                                        </div>
                                      ))
                                    }
                                  </div>
                                )}
                              </div>
                              <div style={{display:'flex',gap:10}}>
                                <button className="btn-cancel" onClick={() => { setEditingChapter(null); setShowModuleDrop(false); }}>取消</button>
                                <button className="btn-save" onClick={() => { handleSaveModule(ch.chapter); setShowModuleDrop(false); }}>保存</button>
                              </div>
                            </span>
                          ) : (
                            <span className="chapter-module" onClick={() => { setEditingChapter(ch.chapter); setEditModule(ch.module || ''); }}>
                              {ch.module || <span className="module-empty">点击映射</span>}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
